import { ref, computed, onMounted, onUnmounted, getCurrentInstance, watch } from 'vue'
import i18n from '@/js/i18n.js'
import { f7 } from 'framework7-vue'
import _ from 'lodash'
import { useAccountStore } from '../pinia-stores/account.js'
import { useAuthStore } from '../pinia-stores/auth.js'

export function useUsersList(options = {}) {
  const { f7route, f7router } = options

  const t = i18n.global.t

  const selectedItem = ref('')
  const description = ref('')
  const autocompleteDropdownAjaxTypeahead = ref(null)

  const accountStore = useAccountStore()
  const authStore = useAuthStore()

  const userAccount = computed(() => accountStore.userAccount)
  const searchedUser = computed(() => accountStore.searchedUser)
  const users = computed(() => accountStore.users)

  const getProfile = computed(() => authStore.getProfile)
  const isAuthenticated = computed(() => authStore.isAuthenticated)
  const isProfileLoaded = computed(() => authStore.isProfileLoaded)
  const getusererror = computed(() => authStore.getusererror)

  onMounted(() => {
    if (isAuthenticated.value) {
      updateList()
      getUserList()
    }

    if (f7.$('#autocomplete-dropdown-user-search').val() === '') {
      accountStore.setUser('')
    }
  })

  watch(isAuthenticated, (auth) => {
    if (auth) {
      updateList()
      getUserList()
    }
  })

  function updateList() {
    if (isAuthenticated.value && users.value.length === 0) {
      accountStore.getAllUsers()
    }
  }

  function getUserList() {
    autocompleteDropdownAjaxTypeahead.value = f7.autocomplete.create({
      inputEl: '#autocomplete-dropdown-user-search',
      openIn: 'dropdown',
      preloader: true,
      preloaderColor: 'multi',
      valueProperty: 'name',
      textProperty: 'name',
      limit: 20,
      typeahead: true,
      dropdownPlaceholderText: t('common.searchAvocashUser'),
      notFoundText: t('common.nothingFound'),
      renderItem(item) {
        if (item.placeholder) {
          return `
              <li class="item-content">
                <div class="item-inner">
                  <div class="item-title-row">
                    <div class="item-title">${item.text}</div>
                  </div>
                </div>
              </li>
            `
        }

        const user = users.value.find((u) => u.name === item.value)
        if (user) Object.assign(item, user)

        const instance = getCurrentInstance()
        const self = instance?.proxy

        const initials = item.name ? self.$filtersGetInitial.getInitial(item.name) : ''
        const pict = item.logo
          ? `<img src="${item.logo}" class="userImage" width="40" alt="user avatar or logo" style="height: 32px; width: 32px; border-radius: 25px"/>`
          : `<span class="badge" style="height: 32px; width: 32px; font-size: 14px; background-color:var(--f7-theme-color)">${initials}</span>`

        return `
            <div class="list media-list">
              <ul class="padding-left no-margin-left">
                <li class="no-margin-left no-padding-left">
                  <label class="item-radio item-content no-margin-left no-padding-left" data-value="${item.value}">
                    <div class="item-media">${pict}</div>
                    <div class="item-inner">
                      <div class="item-title">${item.text}</div>
                      <div class="item-subtitle">${item.username || ''}</div>
                      <div class="item-footer avocash">${item.tag || ' '}</div>
                    </div>
                  </label>
                </li>
              </ul>
            </div>
          `
      },
      source(query, render) {
        const autocomplete = this
        const results = []
        if (!query || query.length === 0) {
          render(results)
          return
        }
        autocomplete.preloaderShow()
        for (const user of users.value) {
          const name = user.name ? user.name.toLowerCase().toString() : ''
          const phoneNumber = user.phoneNumber
            ? user.phoneNumber.toLowerCase().toString()
            : ''
          const email = user.email ? user.email.toString() : ''
          const username = user.username ? user.username.toLowerCase().toString() : ''
          const tag = user.tag ? user.tag.toLowerCase() : ''
          const walletId = user.walletId ? user.walletId.toLowerCase().toString() : ''

          if (
            name.includes(query.toLowerCase()) ||
            phoneNumber.includes(query.toLowerCase()) ||
            (email !== '' &&
              email
                .substring(0, email.indexOf('@'))
                .toLowerCase()
                .includes(query.toLowerCase())) ||
            username.includes(query.toLowerCase()) ||
            tag.includes(query.toLowerCase()) ||
            walletId.includes(query.toLowerCase())
          ) {
            results.push(user)
          }
        }
        render(results)
      },
      on: {
        change(value) {
          accountStore.setUser(value[0])
        },
      },
    })
  }

  function clearSearch() {
    if (f7.$('#autocomplete-dropdown-ajax-typeahead').val() === '') {
      accountStore.setUser('')
    }
    accountStore.setUser('')
  }

  return {
    selectedItem,
    description,
    autocompleteDropdownAjaxTypeahead,
    userAccount,
    searchedUser,
    users,
    getProfile,
    isAuthenticated,
    isProfileLoaded,
    getusererror,
    updateList,
    getUserList,
    clearSearch,
  }
}
